import NotificationIcon from "@/assets/notification-icon.svg?react"
import NotificationWithBellIcon from "@/assets/notification-with-bell-icon.svg?react"
import { I18nEnum } from "@/entities/account/account.types"
import { useAuthStore } from "@/entities/auth/auth.model"
import { acknowledgeAssistantConversation } from "@/entities/assistant/assistant.completion-notifications"
import {
  acknowledgeReviewCompletions,
  completionKey,
  completionPath,
} from "@/entities/notification/completion-alerts"
import {
  acknowledgeCompletionInbox,
  markCompletionInboxSeen,
} from "@/entities/notification/completion-inbox"
import { useCompletionInbox } from "@/entities/notification/completion-inbox.hooks"
import {
  DEFAULT_NOTIFICATION_FORM,
  getCompletionNotificationMessageKey,
  getNotificationMessageKey,
  getNotificationTargetPath,
} from "@/entities/notification/notification.constants"
import { useNotification } from "@/entities/notification/notification.hooks"
import {
  Button,
  getTimeBasedOnDistanceFromNow,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@beringlab/ui"
import toast from "react-hot-toast"
import { Trans, useTranslation } from "react-i18next"
import { Link } from "@tanstack/react-router"
import { useEffect } from "react"
import { CompletionTaskName } from "./completion-task-name"

export const NotificationPopover = () => {
  const { t, i18n } = useTranslation()
  const isKoreanLanguage = i18n.language.startsWith(I18nEnum.Ko)
  const { isAdmin } = useAuthStore()
  const {
    isNotiOpen,
    setIsNotiOpen,
    isUnreadNotiExists,
    notiList,
    pushAlarm,
    clickAlarm,
  } = useNotification()
  const completions = useCompletionInbox()
  useEffect(() => {
    if (isNotiOpen) markCompletionInboxSeen()
  }, [isNotiOpen, completions])
  const items = [
    ...completions.map((message) => ({
      source: "completion" as const,
      message,
    })),
    ...(notiList === DEFAULT_NOTIFICATION_FORM ? [] : notiList).map(
      (message) => ({ source: "push" as const, message })
    ),
  ].sort(
    (a, b) => Date.parse(b.message.createDt) - Date.parse(a.message.createDt)
  )

  return (
    <Popover
      open={isNotiOpen}
      onOpenChange={(open) => {
        if (open) pushAlarm()
        setIsNotiOpen(open)
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="plain"
          size="iconLg"
          aria-label={t("notification.title")}
          className={`${isNotiOpen ? "bg-gray-backdrop-300" : "hover:bg-outline-button-hovered"}`}
          onClick={() => setIsNotiOpen(!isNotiOpen)}
        >
          {isUnreadNotiExists || completions.some((entry) => !entry.isSeen) ? (
            <NotificationWithBellIcon />
          ) : (
            <NotificationIcon />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="max-h-[500px] w-[290px] overflow-auto text-sm">
        <div className="mx-auto flex h-[51px] justify-center border-b border-border-color py-4">
          {t("notification.title")}
        </div>
        <div className="flex flex-col space-y-1 p-1.5">
          {items.length === 0 ? (
            <div className="flex justify-center py-6 text-disabled">
              {t("notification.empty")}
            </div>
          ) : (
            items.map((item) => {
              if (item.source === "completion")
                return (
                  <Link
                    key={`completion-${completionKey(item.message)}`}
                    to={completionPath(item.message)}
                    className="relative flex flex-col rounded-default p-2.5 text-start hover:bg-outline-button-hovered"
                    onClick={() => {
                      const path = completionPath(item.message)
                      acknowledgeCompletionInbox(path)
                      if (item.message.kind === "assistant")
                        acknowledgeAssistantConversation(
                          item.message.resourceId
                        )
                      else acknowledgeReviewCompletions(path)
                      setIsNotiOpen(false)
                    }}
                  >
                    {!item.message.isRead && (
                      <div className="absolute left-1 top-1 h-1 w-1 rounded-full bg-key-color" />
                    )}
                    <div className="font-medium">
                      {t(
                        getCompletionNotificationMessageKey(item.message.kind)
                      )}
                    </div>
                    {item.message.kind === "assistant" && (
                      <CompletionTaskName conversationId={item.message.resourceId} />
                    )}
                    <div className="text-xs text-disabled">
                      {getTimeBasedOnDistanceFromNow({
                        date: item.message.createDt,
                        isKoreanLanguage,
                      })}
                    </div>
                  </Link>
                )
              const msg = item.message
              return (
                <button
                  key={`push-${msg.id}`}
                  className="relative flex flex-col rounded-default p-2.5 text-start hover:bg-outline-button-hovered"
                  onClick={() => {
                    clickAlarm({ pushMsgId: msg.id })
                    const targetPath = getNotificationTargetPath(msg, isAdmin)
                    if (!targetPath) {
                      return toast.error(t("notification.noParameter"))
                    }
                    window.open(
                      `${window.location.origin}${targetPath}`,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }}
                >
                  {!msg.isRead && (
                    <div className="absolute left-1 top-1 h-1 w-1 rounded-full bg-key-color" />
                  )}
                  <div className="font-medium">
                    <Trans
                      i18nKey={getNotificationMessageKey(msg.kind)}
                      components={{ br: <br /> }}
                    />
                  </div>
                  <div className="text-xs text-disabled">
                    {getTimeBasedOnDistanceFromNow({
                      date: msg.createDt,
                      isKoreanLanguage,
                    })}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
